import { emit, events } from '@estee/elc-service-bus';
import { action, computed, observable, reaction } from 'mobx';
import { Product } from '~domain/entities/Product';
import { Shade } from '~domain/entities/Shade';
import { FilterService } from '~domain/services/FilterService';
import { IShadeSwatchesCategories } from '~interfaces/IShadePicker';
import { IFilterData } from '../views/product-filters/IProductFilters';
import { FilterSetTypes } from '~constants/Filters';
import { FilterSet } from '~domain/entities/FilterSet';
import { ProductBaseController } from './ProductBaseController';

export class SkuPickerViewController extends ProductBaseController {
    @observable public isOverlayOpen: boolean = false;
    @observable product: Product;
    @observable private shadeFiltersSettings: IShadeSwatchesCategories = {};
    @observable public isAllFilterOptionSelected: boolean = true;
    @observable public filtersData: FilterSet[] = [];
    public filterService: FilterService;

    constructor(filterService: FilterService) {
        super();
        this.filterService = filterService;

        reaction(
            () => this.appliedFilters,
            () => {
                this.toggleApplicableFilters();
                !this.appliedFilters.length && this.toggleAllFilterOption(true);
            }
        );
    }

    @action
    public initialize(product: Product, filterData?: IFilterData) {
        this.product = product;
        if (filterData) {
            const filterSets = filterData.filterSets;
            this.filtersData = this.filterService.generateFilterSets(filterSets, this.shades);
        }
    }

    @computed
    public get data() {
        return {
            ...this.baseData,
            filteredShades: this.filteredShadesByCategories,
            shadesNumber: this.shadesNumber,
            activeFilterSet: this.activeFilterSet,
            appliedFilters: this.appliedFilters,
            shadeFilters: this.shadeFilters,
            totalAppliedFilters: this.totalAppliedFilters
        };
    }

    @computed
    public get shades() {
        return this.product.shades || [];
    }

    @computed
    public get filteredShadesByCategories() {
        if (!this.appliedFilters.length) {
            return this.shades;
        }

        return this.filterService.filter(this.shades, this.appliedFilters) as Shade[];
    }

    @computed
    public get shadesNumber() {
        return this.filteredShadesByCategories.length;
    }

    @computed
    public get activeFilterSet() {
        const allowAllFiltersCollapsed = this.shadeFiltersSettings.allowAllFiltersCollapsed;
        const type = this.shadeFiltersSettings.filterSets?.type;
        let activeFilter = this.shadeFilters.filter((_filterSet) => _filterSet.isOpen)[0];
        if (!allowAllFiltersCollapsed && type !== FilterSetTypes.ACCORDIONS && !activeFilter) {
            activeFilter = this.shadeFilters[0];
        }

        return activeFilter;
    }

    @computed public get appliedFilters(): FilterSet[] {
        return this.filterService.getAppliedFilterSets(this.filtersData);
    }

    @computed public get shadeFilters() {
        return this.filtersData ?? [];
    }

    @computed public get totalAppliedFilters(): number {
        return this.appliedFilters.reduce((total, filterSet) => {
            return total + filterSet.filters.filter((filter) => filter.active).length;
        }, 0);
    }

    @action
    public closeOverlay = () => {
        this.isOverlayOpen = false;
        emit(events.SKU_PICKER_CLOSED, { productId: this.product.productId });
    };

    @action
    public openOverlay = () => {
        this.isOverlayOpen = true;
        emit(events.SKU_PICKER_OPENED, { productId: this.product.productId });
    };

    @action
    public setShadeFiltersSettings = (shadeFiltersSettings: IShadeSwatchesCategories) => {
        this.shadeFiltersSettings = shadeFiltersSettings;
    };

    @action
    public toggleApplicableFilters = () => {
        this.filtersData = this.filterService.getApplicableFilters(this.filtersData, this.shades);
    };

    @action
    public onClearAllAppliedFilters(filterSets: FilterSet[]) {
        filterSets.forEach((filterSet) => filterSet.clearAllFilters());
    }

    @action
    public toggleAllFilterOption = (isSelected: boolean) => {
        this.isAllFilterOptionSelected = isSelected;
        if (isSelected) {
            // close open filter sets
            this.shadeFilters.forEach(
                (filterSet) => filterSet.isOpen && filterSet.toggleOpenState()
            );
        }
    };

    public handleFilterSetToggle = (filterSet: FilterSet) => {
        const {
            allowAllFiltersCollapsed: allowAllFiltersCollapsedConfig,
            filterSets
        } = this.shadeFiltersSettings;
        const allowAllFiltersCollapsed = !!allowAllFiltersCollapsedConfig;
        const type = filterSets && filterSets.type;
        const activeFilterSets = this.shadeFilters.filter((_filterSet) => _filterSet.isOpen);

        if (type === FilterSetTypes.TOGGLES) {
            const activeFilterClicked =
                activeFilterSets[0] && activeFilterSets[0].key === filterSet.key;

            if (activeFilterClicked && !allowAllFiltersCollapsed) {
                return null;
            }

            filterSet.toggleOpenState();
            if (this.isAllFilterOptionSelected) {
                this.toggleAllFilterOption(false);
            }
            // close all colapsed filters
            activeFilterSets[0] && activeFilterSets[0].setOpenState(false);
        } else if (type === FilterSetTypes.ACCORDIONS) {
            filterSet.toggleOpenState();
        }
    };

    public onAllFilterOptionClick = () => {
        const {
            onClearAllAppliedFilters,
            toggleAllFilterOption,
            shadeFilters,
            activeFilterSet,
            shadeFiltersSettings
        } = this;

        if (shadeFiltersSettings.enableAllFilterCategory && activeFilterSet) {
            // close other active filter sets if ALL is selected
            this.handleFilterSetToggle(activeFilterSet);
        }
        onClearAllAppliedFilters(shadeFilters);
        toggleAllFilterOption(true);
    };
}
